import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { validatePassword } from "../utils/passwordRules";
import { gaEvent, gaSetUserId, gaClearUserId } from "../utils/ga";

const AuthContext = createContext();

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

const mapSupabaseUser = (supabaseUser) => {
  if (!supabaseUser) return null;
  const avatarUrl = supabaseUser.user_metadata?.avatar_url || supabaseUser.user_metadata?.avatarUrl;
  return {
    id: supabaseUser.id,
    email: supabaseUser.email,
    name: supabaseUser.user_metadata?.name || supabaseUser.email?.split("@")[0] || "Usuario",
    photoURL: avatarUrl || null
  };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      const mapped = mapSupabaseUser(session?.user) || null;
      setUser(mapped);
      if (mapped?.id) gaSetUserId(mapped.id);
      setIsLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const mapped = mapSupabaseUser(session?.user) || null;
      setUser(mapped);
      if (mapped?.id) gaSetUserId(mapped.id);
      else gaClearUserId();
    });

    return () => subscription?.unsubscribe();
  }, []);

  const login = useCallback(async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
    if (error) return { error: error.message === "Invalid login credentials" ? "Email o contraseña incorrectos" : error.message };
    if (data?.user) gaEvent("login");
    return { ok: true };
  }, []);

  const register = useCallback(async (name, email, password) => {
    const { allOk } = validatePassword(password);
    if (!allOk) return { error: "La contraseña no cumple las reglas de seguridad" };

    const baseUrl = process.env.REACT_APP_SITE_URL || window.location.origin;
    const emailRedirectTo = `${baseUrl.replace(/\/$/, "")}/`;
    const { data, error } = await supabase.auth.signUp({
      email: email.trim().toLowerCase(),
      password,
      options: {
        data: { name: name.trim() },
        emailRedirectTo
      }
    });
    if (error) return { error: error.message };
    if (data?.user) gaEvent("sign_up");
    return { ok: true };
  }, []);

  const logout = useCallback(async () => {
    gaEvent("logout");
    gaClearUserId();
    await supabase.auth.signOut();
    setUser(null);
  }, []);

  const requestPasswordReset = useCallback(async (email) => {
    const baseUrl = process.env.REACT_APP_SITE_URL || window.location.origin;
    const redirectTo = `${baseUrl.replace(/\/$/, "")}/reset-password`;
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), { redirectTo });
    if (error) return { error: error.message };
    return { ok: true };
  }, []);

  const updatePassword = useCallback(async (newPassword) => {
    const { allOk } = validatePassword(newPassword);
    if (!allOk) return { error: "La contraseña no cumple las reglas de seguridad" };

    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) return { error: error.message };
    return { ok: true };
  }, []);

  const updateProfile = useCallback(async (updates) => {
    const { data: { user: u }, error } = await supabase.auth.updateUser({ data: updates });
    if (error) return { error: error.message };
    setUser(mapSupabaseUser(u));
    return { ok: true };
  }, []);

  const uploadAvatar = useCallback(async (file) => {
    const { data: { user: u } } = await supabase.auth.getUser();
    if (!u) return { error: "No hay sesión" };
    const ext = file.name.split(".").pop()?.toLowerCase() || "jpg";
    const path = `${u.id}/avatar.${ext}`;
    const { error: uploadError } = await supabase.storage.from("avatars").upload(path, file, { upsert: true });
    if (uploadError) {
      const msg = uploadError.message?.toLowerCase?.();
      if (msg?.includes("bucket") || msg?.includes("not found") || msg?.includes("no encontrado")) {
        return { error: "Crea el bucket 'avatars' en Supabase: Storage → New bucket. Ver docs/supabase-storage-avatars.sql" };
      }
      return { error: uploadError.message };
    }
    const { data: { publicUrl } } = supabase.storage.from("avatars").getPublicUrl(path);
    const result = await updateProfile({ avatar_url: publicUrl });
    if (result.error) return result;
    return { ok: true };
  }, [updateProfile]);

  const removeAvatar = useCallback(async () => {
    const result = await updateProfile({ avatar_url: "" });
    if (result.error) return result;
    return { ok: true };
  }, [updateProfile]);

  const value = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    logout,
    register,
    requestPasswordReset,
    updatePassword,
    updateProfile,
    uploadAvatar,
    removeAvatar
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
